/**
 * DepositReturnModal — บันทึกการคืนมัดจำ / คงมัดจำไว้ก่อน (Deposit v2 Phase 1)
 *
 * เปิดจากปุ่ม "บันทึกการคืนมัดจำ" ในหน้ารายละเอียดการเช่า (ต้องมี rentals.deposit_manage)
 * - โหมดปกติ  → POST /rentals/{id}/items/{item}/deposit/return  {returned_date, refund_method, refund_reference, note, release_hold}
 * - โหมด hold → ติ๊ก "ยังไม่รับเงินมัดจำคืน (คงมัดจำไว้ก่อน)" → POST .../deposit/hold {note}  (หมายเหตุ = เหตุผลที่คงไว้, บังคับกรอก)
 * - item ที่ hold อยู่ → banner ม่วง + checkbox "ปลดการคงมัดจำและบันทึกคืน" (release_hold=true) ไม่งั้น backend 409 DEPOSIT_ON_HOLD
 * - เกินกำหนด (deposit_refund_overdue จาก backend) → banner เหลืองเตือน ไม่บล็อค
 * - warnings[] จาก response → showAlert warning หลังบันทึกสำเร็จ (ไม่บล็อค)
 * - error (409/400/403) → แสดง error.user_message||message inline ใน modal
 */
import React, { useEffect, useState } from 'react';
import { RiAlertLine, RiLockLine, RiMoneyDollarCircleLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import { cn } from '../../utils/cn';
import { showAlert } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { getLocalDateString } from '../../utils/dateUtils';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { useHoldDeposit, useReturnDeposit } from '../../hooks/mutations/useDepositMutations';
import {
  DEPOSIT_REFUND_METHOD_OPTIONS,
  DEPOSIT_REFUND_SLA_BUSINESS_DAYS,
  DepositActionResponse,
  DepositRefundMethod,
  RentalItemDepositFields,
  formatDateDMY,
} from '../../services/depositService';

/** ข้อมูล item เท่าที่ modal ต้องใช้แสดงหัว (ชื่อสินค้า/SN/ยอด) — รับได้ทั้ง shape ของ view page และ service */
export type DepositModalItem = RentalItemDepositFields & {
  id: number;
  product_name?: string | null;
  product?: { id?: number; name: string } | null;
  device_serial?: string | null;
  device?: { serial_number?: string | null; code?: string | null } | null;
  is_software_only?: boolean;
};

interface DepositReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalId: number;
  rentalStatus?: string | null;
  item: DepositModalItem | null;
  onSuccess?: (response: DepositActionResponse) => void;
  /** SLA ที่ใช้ในข้อความเตือน (ค่าจริงอยู่ที่ backend) */
  slaBusinessDays?: number;
}

const MAX_REFERENCE = 100;
const MAX_NOTE = 500;

export const getDepositItemName = (item: DepositModalItem): string =>
  item.product_name || item.product?.name || 'ไม่ระบุสินค้า';

export const getDepositItemSerial = (item: DepositModalItem): string | null => {
  if (item.is_software_only) return null;
  return item.device?.serial_number || item.device_serial || null;
};

const DepositReturnModal: React.FC<DepositReturnModalProps> = ({
  isOpen,
  onClose,
  rentalId,
  rentalStatus,
  item,
  onSuccess,
  slaBusinessDays = DEPOSIT_REFUND_SLA_BUSINESS_DAYS,
}) => {
  const [returnedDate, setReturnedDate] = useState('');
  const [holdMode, setHoldMode] = useState(false);
  const [releaseHold, setReleaseHold] = useState(false);
  const [refundMethod, setRefundMethod] = useState<DepositRefundMethod>('BANK_TRANSFER');
  const [refundReference, setRefundReference] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const returnMutation = useReturnDeposit();
  const holdMutation = useHoldDeposit();
  const submitGuard = useSubmitGuard();

  const today = getLocalDateString();
  const isOnHold = !!item && item.deposit_status === 'PENDING_REFUND' && !!item.deposit_hold;
  const pendingSince = item?.deposit_refund_pending_since || null;
  const paidDate = item?.deposit_paid_date || null;
  // วันที่คืนต้อง ≥ paid_date และ ≥ pending_since (I5) — ใช้ค่าที่มากกว่าเป็น min (ตัดเป็น YYYY-MM-DD เผื่อ API ส่ง datetime)
  const minDate =
    [pendingSince, paidDate]
      .filter((v): v is string => !!v)
      .map((v) => v.slice(0, 10))
      .sort()
      .pop() || undefined;
  const isOverdue = !!item?.deposit_refund_overdue && !isOnHold;
  const isPaidOnLiveRental =
    item?.deposit_status === 'PAID' && rentalStatus !== 'COMPLETED' && rentalStatus !== 'CANCELLED';

  // reset ทุกครั้งที่เปิด
  useEffect(() => {
    if (isOpen) {
      setReturnedDate(getLocalDateString());
      setHoldMode(false);
      setReleaseHold(false);
      setRefundMethod('BANK_TRANSFER');
      setRefundReference('');
      setNote('');
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, item?.id]);

  const loading = returnMutation.isPending || holdMutation.isPending;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (holdMode) {
      if (!note.trim()) next.note = 'กรุณาระบุเหตุผล/เงื่อนไขที่คงมัดจำไว้';
    } else {
      if (!returnedDate) {
        next.returned_date = 'กรุณาระบุวันที่คืนมัดจำ';
      } else {
        if (returnedDate > today) next.returned_date = 'วันที่คืนมัดจำต้องไม่เกินวันนี้';
        else if (minDate && returnedDate < minDate) {
          next.returned_date = `วันที่คืนมัดจำต้องไม่ก่อน ${formatDateDMY(minDate)}`;
        }
      }
      if (isOnHold && !releaseHold) {
        next.release_hold = 'รายการนี้คงมัดจำไว้อยู่ — ติ๊ก "ปลดการคงมัดจำและบันทึกคืน" ก่อนบันทึก';
      }
      if (refundReference.length > MAX_REFERENCE) next.refund_reference = `เลขอ้างอิงยาวได้ไม่เกิน ${MAX_REFERENCE} ตัวอักษร`;
    }
    if (note.length > MAX_NOTE) next.note = `หมายเหตุยาวได้ไม่เกิน ${MAX_NOTE} ตัวอักษร`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!item) return;
    setSubmitError(null);
    if (!validate()) return;
    if (!submitGuard.begin()) return;
    try {
      let response: DepositActionResponse;
      if (holdMode) {
        response = await holdMutation.mutateAsync({
          rentalId,
          itemId: item.id,
          data: { note: note.trim() },
        });
      } else {
        response = await returnMutation.mutateAsync({
          rentalId,
          itemId: item.id,
          data: {
            returned_date: returnedDate,
            refund_method: refundMethod,
            refund_reference: refundReference.trim() || undefined,
            note: note.trim() || undefined,
            ...(isOnHold ? { release_hold: true } : {}),
          },
        });
      }
      if (response?.warnings?.length) {
        showAlert('บันทึกแล้ว — มีข้อควรทราบ', response.warnings.join('\n'), 'warning');
      }
      onSuccess?.(response);
      onClose();
    } catch (error: any) {
      // KRP error envelope ใส่ dict detail ของ HTTPException ไว้ที่ error.details (error_handler.handle_http_exception)
      // → reason อยู่ที่ error.details.reason (error.reason เผื่อ shape เก่า) — E2E Deposit v2 OBS-2
      const envelope = error?.response?.data?.error;
      const reason = envelope?.details?.reason ?? envelope?.reason;
      const message = getApiErrorMessage(
        error,
        holdMode ? 'ไม่สามารถบันทึกคงมัดจำได้' : 'ไม่สามารถบันทึกการคืนมัดจำได้'
      );
      if (reason === 'DEPOSIT_ON_HOLD') {
        const hint = 'ติ๊ก "ปลดการคงมัดจำและบันทึกคืน" แล้วบันทึกอีกครั้ง';
        if (isOnHold) {
          setSubmitError(message);
          setErrors((prev) => ({ ...prev, release_hold: hint }));
        } else {
          // modal เปิดจากข้อมูลเก่า (รายการเพิ่งถูกคงมัดจำจากที่อื่น) — banner/checkbox ปลด hold ยังไม่แสดง
          // → บอกในกล่อง error ให้เปิดใหม่ก่อน (validate ฝั่ง client บล็อคเคส hold ที่รู้อยู่แล้ว)
          setSubmitError(`${message} — ปิดหน้าต่างแล้วเปิดใหม่เพื่อโหลดสถานะล่าสุด จากนั้น${hint}`);
        }
      } else {
        setSubmitError(message);
      }
    } finally {
      submitGuard.end();
    }
  };

  if (!item) return null;

  const serial = getDepositItemSerial(item);
  const amount = Number(item.deposit_amount || 0);
  const detailFieldsDisabled = loading || holdMode;

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        disabled={loading}
      >
        ยกเลิก
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        className={cn(
          'px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center',
          holdMode
            ? 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
            : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
        )}
        disabled={loading}
      >
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {holdMode ? 'บันทึกคงมัดจำ' : 'บันทึกการคืนมัดจำ'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="บันทึกการคืนมัดจำ"
      size="lg"
      icon={<RiMoneyDollarCircleLine className="w-5 h-5" />}
      footer={footer}
      closeOnClickOutside={!loading}
      closeOnEsc={!loading}
    >
      <div className="space-y-4">
        {/* หัว: รายการ / SN / ยอด / ยกเลิก-จบเมื่อ */}
        <div className="bg-secondary-50 rounded-lg p-3 sm:p-4 text-sm space-y-1">
          <p className="font-medium text-secondary-900 break-words">{getDepositItemName(item)}</p>
          {serial && (
            <p className="text-secondary-600">
              Serial Number: <span className="font-mono">{serial}</span>
            </p>
          )}
          <p className="text-secondary-600">
            ยอดมัดจำ: <span className="font-semibold text-secondary-900">฿{amount.toLocaleString()}</span>
          </p>
          {pendingSince && (
            <p className="text-secondary-600">
              ยกเลิก-จบเมื่อ {formatDateDMY(pendingSince)}
              {item.deposit_refund_business_days_elapsed !== null && item.deposit_refund_business_days_elapsed !== undefined && (
                <> (วันทำการที่ {item.deposit_refund_business_days_elapsed})</>
              )}
            </p>
          )}
        </div>

        {/* banner ม่วง: item hold อยู่ */}
        {isOnHold && (
          <div className="rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800 flex gap-2" data-testid="deposit-hold-banner">
            <RiLockLine className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-medium">รายการนี้คงมัดจำไว้อยู่</p>
              <p className="text-purple-700">
                {item.deposit_hold_at && <>ตั้งแต่ {formatDateDMY(item.deposit_hold_at)}</>}
                {item.deposit_hold_note && <> · {item.deposit_hold_note}</>}
              </p>
              <label className="mt-2 flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={releaseHold}
                  onChange={(e) => {
                    setReleaseHold(e.target.checked);
                    setErrors((prev) => ({ ...prev, release_hold: '' }));
                  }}
                  disabled={loading}
                  className="mt-0.5 w-4 h-4 text-purple-600 border-secondary-300 rounded focus:ring-purple-500"
                />
                <span className="font-medium">ปลดการคงมัดจำและบันทึกคืน</span>
              </label>
              {errors.release_hold && <p className="mt-1 text-sm text-danger-600">{errors.release_hold}</p>}
            </div>
          </div>
        )}

        {/* banner เหลือง: เกินกำหนด (ไม่บล็อค) */}
        {isOverdue && (
          <div className="rounded-md border border-warning-300 bg-warning-50 p-3 text-sm text-warning-800 flex gap-2" data-testid="deposit-overdue-banner">
            <RiAlertLine className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              เกินกำหนดคืนมัดจำ {slaBusinessDays} วันทำการแล้ว
              {item.deposit_refund_due_date && <> (กำหนด {formatDateDMY(item.deposit_refund_due_date)})</>}
              {' '}— ยังบันทึกได้ ระบบจะบันทึกว่าคืนล่าช้า
            </p>
          </div>
        )}

        {/* banner: PAID บน rental ที่ยังเดินอยู่ */}
        {isPaidOnLiveRental && (
          <div className="rounded-md border border-info-200 bg-info-50 p-3 text-sm text-info-800 flex gap-2">
            <RiAlertLine className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>การเช่ายังใช้งานอยู่ — โดยปกติจะคืนมัดจำหลังยกเลิก/จบการเช่า</p>
          </div>
        )}

        {/* submit error */}
        {submitError && (
          <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700" role="alert">
            {submitError}
          </div>
        )}

        {/* ฟอร์ม */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="deposit-returned-date" className="block text-sm font-medium text-secondary-700">
              วันที่คืนมัดจำ {!holdMode && <span className="text-danger-500">*</span>}
            </label>
            <input
              id="deposit-returned-date"
              type="date"
              value={returnedDate}
              min={minDate}
              max={today}
              onChange={(e) => {
                setReturnedDate(e.target.value);
                setErrors((prev) => ({ ...prev, returned_date: '' }));
              }}
              disabled={detailFieldsDisabled}
              className={cn('input mt-1', errors.returned_date && 'border-danger-500', detailFieldsDisabled && 'bg-secondary-50 cursor-not-allowed')}
              aria-invalid={Boolean(errors.returned_date)}
            />
            {errors.returned_date && <p className="mt-1 text-sm text-danger-600">{errors.returned_date}</p>}

            {/* checkbox คงมัดจำไว้ก่อน — ซ่อนเมื่อ item hold อยู่แล้ว */}
            {!isOnHold && (
              <label className="mt-2 flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={holdMode}
                  onChange={(e) => {
                    setHoldMode(e.target.checked);
                    setErrors({});
                    setSubmitError(null);
                  }}
                  disabled={loading}
                  className="mt-0.5 w-4 h-4 text-purple-600 border-secondary-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-secondary-700">
                  ยังไม่รับเงินมัดจำคืน (คงมัดจำไว้ก่อน)
                  <span className="block text-xs text-secondary-500">หยุดนับกำหนดคืน จนกว่าจะปลดการคงมัดจำ</span>
                </span>
              </label>
            )}
          </div>

          <div>
            <label htmlFor="deposit-refund-method" className="block text-sm font-medium text-secondary-700">วิธีคืน</label>
            <select
              id="deposit-refund-method"
              value={refundMethod}
              onChange={(e) => setRefundMethod(e.target.value as DepositRefundMethod)}
              disabled={detailFieldsDisabled}
              className={cn('select mt-1', detailFieldsDisabled && 'bg-secondary-50 cursor-not-allowed')}
            >
              {DEPOSIT_REFUND_METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="deposit-refund-reference" className="block text-sm font-medium text-secondary-700">เลขอ้างอิง</label>
            <input
              id="deposit-refund-reference"
              type="text"
              value={refundReference}
              maxLength={MAX_REFERENCE}
              onChange={(e) => setRefundReference(e.target.value)}
              disabled={detailFieldsDisabled}
              placeholder="เลขที่โอน / เลขเช็ค"
              className={cn('input mt-1', errors.refund_reference && 'border-danger-500', detailFieldsDisabled && 'bg-secondary-50 cursor-not-allowed')}
            />
            {errors.refund_reference && <p className="mt-1 text-sm text-danger-600">{errors.refund_reference}</p>}
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="deposit-note" className="block text-sm font-medium text-secondary-700">
              {holdMode ? (
                <>เหตุผล/เงื่อนไขที่คงไว้ <span className="text-danger-500">*</span></>
              ) : (
                'หมายเหตุ'
              )}
            </label>
            <textarea
              id="deposit-note"
              value={note}
              maxLength={MAX_NOTE}
              rows={3}
              onChange={(e) => {
                setNote(e.target.value);
                setErrors((prev) => ({ ...prev, note: '' }));
              }}
              disabled={loading}
              placeholder={holdMode ? 'เช่น รอหักค่าเสียหาย / รอเอกสารจากลูกค้า' : 'หมายเหตุเพิ่มเติม (ถ้ามี)'}
              className={cn('input mt-1', errors.note && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
              aria-invalid={Boolean(errors.note)}
            />
            {errors.note && <p className="mt-1 text-sm text-danger-600">{errors.note}</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DepositReturnModal;
